import React, { useState, useEffect } from "react";
import { Upload, User, Mail, Phone, Building, GraduationCap, Target, FileText, MessageCircle, Send, MapPin, Globe } from "lucide-react";
import '../../style/legacy/ContactForm.css';

const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    institution: "",
    college: "",
    purpose: "",
    researchArea: "",
    file: null,
  });
  
  const [errors, setErrors] = useState({});
  const [fileName, setFileName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Simple validation
  const validate = (field, value) => {
    switch (field) {
      case "email":
        return /\S+@\S+\.\S+/.test(value) ? "" : "Enter a valid email";
      case "phone":
        return /^\d{10,}$/.test(value) ? "" : "Enter a valid phone number";
      default:
        return value ? "" : "This field is required";
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    let newValue = files ? files[0] : value;
    setForm({ ...form, [name]: newValue });
    setErrors({ ...errors, [name]: validate(name, value) });
    if (files && files[0]) {
      setFileName(files[0].name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Validate all fields
    const newErrors = {};
    Object.entries(form).forEach(([field, value]) => {
      if (field !== "file") {
        newErrors[field] = validate(field, value);
      }
    });
    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some((err) => err);
    
    if (!hasErrors) {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert("Form submitted successfully!");
      setForm({
        name: "",
        email: "",
        phone: "",
        institution: "",
        college: "",
        purpose: "",
        researchArea: "",
        file: null,
      });
      setFileName("");
    }
    setIsSubmitting(false);
  };

  // Animated background effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      const cards = document.querySelectorAll('.card');
      cards.forEach(card => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        card.style.setProperty('--mouse-x', `${x}px`);
        card.style.setProperty('--mouse-y', `${y}px`);
      });
    };
    
    const cardsContainer = document.getElementById('cards');
    if (cardsContainer) {
      cardsContainer.addEventListener('mousemove', handleMouseMove);
    }
    
    return () => {
      if (cardsContainer) {
        cardsContainer.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, []);

  const fieldConfigs = [
    { name: "name", label: "Full Name", icon: User, type: "text" },
    { name: "email", label: "Email Address", icon: Mail, type: "email" },
    { name: "phone", label: "Phone Number", icon: Phone, type: "tel" },
    { name: "institution", label: "Institution", icon: Building, type: "text" },
    { name: "college", label: "College/University", icon: GraduationCap, type: "text" },
    { name: "purpose", label: "Purpose of Contact", icon: Target, type: "text" },
  ];

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4 py-8 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 z-0" id="cards">
        <div className="card absolute top-1/4 left-1/4 w-64 h-64 bg-blue-200/30 rounded-full blur-xl opacity-70 animate-float"></div>
        <div className="card absolute top-1/3 right-1/4 w-80 h-80 bg-purple-200/30 rounded-full blur-xl opacity-50 animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="card absolute bottom-1/4 left-1/3 w-72 h-72 bg-cyan-200/30 rounded-full blur-xl opacity-60 animate-float" style={{ animationDelay: '4s' }}></div>
      </div>
      
      <div className="relative z-10 w-full max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8 sm:mb-12 px-4">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">Contact Research Lens</h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Get in touch with our team for inquiries about research opportunities, 
            collaborations, or any questions you may have.
          </p>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-6 sm:gap-8">
          {/* Contact Information Card */}
          <div className="card bg-white rounded-2xl shadow-xl p-8 lg:w-2/5 h-fit">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
              <MessageCircle className="w-6 h-6 mr-2 text-blue-600" />
              Get In Touch
            </h2>
            
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="bg-blue-100 p-3 rounded-lg mr-4">
                  <MapPin className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Our Location</h3>
                  <p className="text-gray-600">369 Kalpana Nagar<br />Bhopal </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-blue-100 p-3 rounded-lg mr-4">
                  <Phone className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">6268558700</h3>
                  <p className="text-gray-600">Mon-Sat, 10:00 AM - 6:00 PM</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-blue-100 p-3 rounded-lg mr-4">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Email Address</h3>
                  <p className="text-gray-600">aeggrouphelpdesk@gmail.com<br />aegresearchlens@gmail.com</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-blue-100 p-3 rounded-lg mr-4">
                  <Globe className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Website</h3>
                  <a href="https://www.aegresearch.in" className="text-gray-600 hover:text-blue-600 cursor-pointer hover:underline">aegresearch.in<br /></a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Form Card */}
          <div className="card bg-white rounded-2xl shadow-xl p-8 lg:w-3/5">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
              <Send className="w-6 h-6 mr-2 text-blue-600" />
              Send us a Message
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {fieldConfigs.map(({ name, label, icon: Icon, type }) => (
                  <div key={name} className="space-y-2">
                    <label className="flex items-center text-gray-700 font-medium text-sm">
                      <Icon className="w-4 h-4 mr-2 text-blue-600" />
                      {label}
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type={type}
                      name={name}
                      value={form[name]}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-0 ${
                        errors[name] 
                          ? "border-red-300 bg-red-50" 
                          : "border-gray-300 hover:border-blue-300"
                      }`}
                      placeholder={`Enter your ${label.toLowerCase()}`}
                    />
                    {errors[name] && (
                      <div className="text-red-500 text-sm flex items-center">
                        <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                        {errors[name]}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Research Area */}
              <div className="space-y-2">
                <label className="flex items-center text-gray-700 font-medium text-sm">
                  <FileText className="w-4 h-4 mr-2 text-blue-600" />
                  Research Area/Topic/Branch
                </label>
                <input
                  type="text"
                  name="researchArea"
                  value={form.researchArea}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 transition-all duration-200 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your research area (optional)"
                />
              </div>
              
              {/* File Upload */}
              <div className="space-y-2">
                <label className="flex items-center text-gray-700 font-medium text-sm">
                  <Upload className="w-4 h-4 mr-2 text-blue-600" />
                  File Upload
                </label>
                <div className="relative">
                  <input
                    type="file"
                    name="file"
                    onChange={handleChange}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition-all duration-200 flex items-center justify-center space-x-2 bg-gray-50 hover:bg-blue-50"
                  >
                    <Upload className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-600">
                      {fileName ? "Change file" : "Choose file"}
                    </span>
                  </label>
                  {fileName && (
                    <div className="mt-2 text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                      <span className="font-medium">Selected:</span> {fileName}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-4 rounded-lg font-bold text-lg transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-blue-300 ${
                    isSubmitting
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl"
                  }`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    "Submit Message"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;